import { generateOptions, randInt } from "./questions";
import type { Question } from "./types";
import type { KumonCategoryId } from "./kumonTypes";

export function countCarries(a: number, b: number): number {
  let carries = 0;
  let carry = 0;
  let curA = a;
  let curB = b;
  while (curA > 0 || curB > 0 || carry > 0) {
    const sum = (curA % 10) + (curB % 10) + carry;
    if (sum >= 10) {
      carries++;
      carry = 1;
    } else {
      carry = 0;
    }
    curA = Math.floor(curA / 10);
    curB = Math.floor(curB / 10);
  }
  return carries;
}

export function countBorrows(a: number, b: number): number {
  if (a < b) return 0;
  let borrows = 0;
  let borrow = 0;
  let curA = a;
  let curB = b;
  while (curA > 0 || curB > 0) {
    const da = (curA % 10) - borrow;
    const db = curB % 10;
    if (da < db) {
      borrows++;
      borrow = 1;
    } else {
      borrow = 0;
    }
    curA = Math.floor(curA / 10);
    curB = Math.floor(curB / 10);
  }
  return borrows;
}

export function generateKumonQuestion(catId: KumonCategoryId, prev?: Question): Question {
  let attempts = 0;
  let q: Question;

  do {
    q = generateSingleKumonQuestion(catId);
    attempts++;
  } while (prev && q.a === prev.a && q.b === prev.b && q.op === prev.op && attempts < 30);

  return q;
}

function generateSingleKumonQuestion(catId: KumonCategoryId): Question {
  switch (catId) {
    // 2.1 ระดับ 3A: บวกไม่เกิน +3 โดย +1 ตัวตั้ง<=1000, +2 ตัวตั้ง<=32, +3 ตัวตั้ง<=12
    case "kumon_3a": {
      const b = randInt(1, 3);
      let a: number;
      if (b === 1) {
        // Weighted distribution for +1: mostly 1..1000
        a = randInt(1, 1000);
      } else if (b === 2) {
        a = randInt(1, 32);
      } else {
        a = randInt(1, 12);
      }
      const answer = a + b;
      return { a, b, op: "add", answer, options: generateOptions("add", a, b, answer) };
    }

    // 2.2 ระดับ 2A: บวกไม่เกิน +10 และผลบวกไม่เกิน 20
    case "kumon_2a": {
      const b = randInt(1, 10);
      const a = randInt(1, 20 - b);
      const answer = a + b;
      return { a, b, op: "add", answer, options: generateOptions("add", a, b, answer) };
    }

    // 2.3.1 ระดับ A บวก: บวกเลขสองหลัก ผลบวก <= 28
    case "kumon_a_add": {
      const a = randInt(10, 27);
      const b = randInt(1, 28 - a);
      const answer = a + b;
      return { a, b, op: "add", answer, options: generateOptions("add", a, b, answer) };
    }

    // 2.3.2 ระดับ A ลบ: ตัวตั้งไม่เกิน 20 ตัวลบ -1, -2, -3, -4, -5
    case "kumon_a_sub": {
      const b = randInt(1, 5);
      const a = randInt(b, 20);
      const answer = a - b;
      return { a, b, op: "sub", answer, options: generateOptions("sub", b, answer, answer) };
    }

    // 2.3.3 ระดับ A ผสม
    case "kumon_a_mix": {
      const isAdd = Math.random() < 0.5;
      if (isAdd) {
        return generateSingleKumonQuestion("kumon_a_add");
      } else {
        return generateSingleKumonQuestion("kumon_a_sub");
      }
    }

    // 2.4.1 ระดับ B บวก ง่าย: ผลบวกถึง 100 มีทด 1 ครั้ง
    case "kumon_b_add_easy": {
      for (let i = 0; i < 100; i++) {
        const a = randInt(10, 89);
        const b = randInt(1, 100 - a);
        if (a + b <= 100 && countCarries(a, b) === 1) {
          const answer = a + b;
          return { a, b, op: "add", answer, options: generateOptions("add", a, b, answer) };
        }
      }
      // Fallback
      const a = 28;
      const b = 15;
      return { a, b, op: "add", answer: 43, options: generateOptions("add", a, b, 43) };
    }

    // 2.4.1 ระดับ B บวก ปานกลาง: บวกเลขสองหลักต้องทด 1-2 ครั้ง
    case "kumon_b_add_medium": {
      for (let i = 0; i < 100; i++) {
        const a = randInt(10, 99);
        const b = randInt(10, 99);
        const carries = countCarries(a, b);
        if (carries >= 1 && carries <= 2) {
          const answer = a + b;
          return { a, b, op: "add", answer, options: generateOptions("add", a, b, answer) };
        }
      }
      const a = 47;
      const b = 38;
      return { a, b, op: "add", answer: 85, options: generateOptions("add", a, b, 85) };
    }

    // 2.4.1 ระดับ B บวก ยาก: บวกเลข 3 หลักต้องทด 1-3 ครั้ง
    case "kumon_b_add_hard": {
      for (let i = 0; i < 100; i++) {
        const a = randInt(100, 999);
        const b = randInt(10, 999);
        const carries = countCarries(a, b);
        if (carries >= 1 && carries <= 3) {
          const answer = a + b;
          return { a, b, op: "add", answer, options: generateOptions("add", a, b, answer) };
        }
      }
      const a = 368;
      const b = 275;
      return { a, b, op: "add", answer: 643, options: generateOptions("add", a, b, 643) };
    }

    // 2.4.2 ระดับ B ลบ ง่าย: ตัวตั้ง <= 20 ลบไม่เกิน -9
    case "kumon_b_sub_easy": {
      const b = randInt(1, 9);
      const a = randInt(Math.max(b, 10), 20); // Focus on numbers <= 20 with single digit sub
      const answer = a - b;
      return { a, b, op: "sub", answer, options: generateOptions("sub", b, answer, answer) };
    }

    // 2.4.2 ระดับ B ลบ ปานกลาง: ลบเลข 2 หลักต้องยืม 1 ครั้ง
    case "kumon_b_sub_medium": {
      for (let i = 0; i < 100; i++) {
        const a = randInt(11, 99);
        const b = randInt(2, a - 1);
        if (countBorrows(a, b) === 1) {
          const answer = a - b;
          return { a, b, op: "sub", answer, options: generateOptions("sub", b, answer, answer) };
        }
      }
      const a = 52;
      const b = 27;
      return { a, b, op: "sub", answer: 25, options: generateOptions("sub", b, answer, 25) };
    }

    // 2.4.2 ระดับ B ลบ ยาก: ลบเลข 3 หลัก ต้องยืม 1-2 ครั้ง
    case "kumon_b_sub_hard": {
      for (let i = 0; i < 100; i++) {
        const a = randInt(100, 999);
        const b = randInt(10, a - 1);
        const borrows = countBorrows(a, b);
        if (borrows >= 1 && borrows <= 2) {
          const answer = a - b;
          return { a, b, op: "sub", answer, options: generateOptions("sub", b, answer, answer) };
        }
      }
      const a = 512;
      const b = 278;
      return { a, b, op: "sub", answer: 234, options: generateOptions("sub", b, answer, 234) };
    }
  }
}

export function generateKumonQuestionSet(catId: KumonCategoryId, count: number): Question[] {
  const list: Question[] = [];
  for (let i = 0; i < count; i++) {
    const prev = list[list.length - 1];
    list.push(generateKumonQuestion(catId, prev));
  }
  return list;
}
